// Image optimization utilities for Cloudinary URLs
export const getOptimizedImageUrl = (originalUrl, options = {}) => {
  if (!originalUrl || !originalUrl.includes('cloudinary.com')) {
    return originalUrl;
  }

  const {
    width,
    height,
    quality = 'auto:good',
    format = 'auto',
    crop = 'fill',
    gravity = 'center',
    fetchFormat = 'auto',
    progressive = true,
    blur,
    brightness,
    contrast,
    saturation
  } = options;

  try {
    // Parse the Cloudinary URL
    const urlParts = originalUrl.split('/');
    const uploadIndex = urlParts.findIndex(part => part === 'upload');

    if (uploadIndex === -1) {
      return originalUrl;
    }

    // Build transformation string
    const transformations = [];

    // Quality and format optimizations
    transformations.push(`q_${quality}`);
    transformations.push(`f_${format}`);

    if (fetchFormat !== 'auto') {
      transformations.push(`fl_${fetchFormat}`);
    }

    // Progressive loading for better perceived performance
    if (progressive) {
      transformations.push('fl_progressive');
    }

    // Responsive sizing
    if (width || height) {
      let sizeTransform = 'c_' + crop;
      if (width) sizeTransform += `,w_${width}`;
      if (height) sizeTransform += `,h_${height}`;
      if (gravity !== 'center') sizeTransform += `,g_${gravity}`;
      transformations.push(sizeTransform);
    }

    // Color adjustments
    if (blur) transformations.push(`e_blur:${blur}`);
    if (brightness) transformations.push(`e_brightness:${brightness}`);
    if (contrast) transformations.push(`e_contrast:${contrast}`);
    if (saturation) transformations.push(`e_saturation:${saturation}`);

    // Insert transformations into URL
    const transformationString = transformations.join(',');
    urlParts.splice(uploadIndex + 1, 0, transformationString);

    return urlParts.join('/');
  } catch (error) {
    console.warn('Error optimizing image URL:', error);
    return originalUrl;
  }
};

// Responsive image URLs for different breakpoints
export const getResponsiveImageUrls = (originalUrl) => {
  if (!originalUrl) return {};

  return {
    thumbnail: getOptimizedImageUrl(originalUrl, {
      width: 150,
      height: 150,
      quality: 'auto:low',
      crop: 'thumb'
    }),
    small: getOptimizedImageUrl(originalUrl, {
      width: 400,
      quality: 'auto:good',
      crop: 'fill'
    }),
    medium: getOptimizedImageUrl(originalUrl, {
      width: 800,
      quality: 'auto:good',
      crop: 'fill'
    }),
    large: getOptimizedImageUrl(originalUrl, {
      width: 1200,
      quality: 'auto:good',
      crop: 'fill'
    }),
    xlarge: getOptimizedImageUrl(originalUrl, {
      width: 1920,
      quality: 'auto:best',
      crop: 'fill'
    })
  };
};

// Generate srcSet for responsive images
export const generateSrcSet = (originalUrl) => {
  if (!originalUrl) return '';

  const sizes = [400, 800, 1200, 1920];
  const srcSetEntries = sizes.map(width => {
    const optimizedUrl = getOptimizedImageUrl(originalUrl, {
      width,
      quality: 'auto:good',
      crop: 'fill'
    });
    return `${optimizedUrl} ${width}w`;
  });

  return srcSetEntries.join(', ');
};

// Lazy loading image with blur placeholder
export const createBlurPlaceholder = (originalUrl) => {
  return getOptimizedImageUrl(originalUrl, {
    width: 40,
    height: 30,
    quality: 'auto:low',
    blur: 1000,
    crop: 'fill'
  });
};

// Optimize gallery images for slideshow
export const optimizeGalleryImage = (originalUrl, isMainImage = false, isThumbnail = false) => {
  if (isThumbnail) {
    return getOptimizedImageUrl(originalUrl, {
      width: 120,
      height: 80,
      quality: 'auto:good',
      crop: 'fill',
      gravity: 'center'
    });
  }

  if (isMainImage) {
    return getOptimizedImageUrl(originalUrl, {
      width: 1200,
      height: 800,
      quality: 'auto:best',
      crop: 'fit',
      gravity: 'center'
    });
  }

  return getOptimizedImageUrl(originalUrl, {
    width: 800,
    height: 600,
    quality: 'auto:good',
    crop: 'fit',
    gravity: 'center'
  });
};